/**
 * Animal Entities - Betrieb Müller
 *
 * Individual livestock as Digital Twin entities, identified by ear tag (Ohrenmarke).
 */

import type { AnimalEntity } from '@hofsync/design-system';
import { daysAgo } from '../farm-profile';

// --- Cattle (Rinder) ---

export const ANIMAL_BELLA: AnimalEntity = {
  id: 'animal-bella',
  type: 'animal',
  name: 'Bella',
  status: 'active',
  lastUpdated: daysAgo(1),
  properties: {
    ear_tag: 'DE 034 0123 45601',
    species: 'cattle',
    animal_type: 'Milchkuh',
    breed: 'Holstein-Friesian',
    date_of_birth: '2019-03-12',
    sex: 'female',
    weight: '680 kg',
    housing: 'Laufstall',
    hi_tier_nr: 'DE 034 0123456',
  },
  documents: [
    {
      id: 'doc-hitier-bella',
      name: 'HI-Tier Meldung - Bella',
      type: 'nachweis',
      date: daysAgo(30),
      source: 'HI-Tier Portal',
      status: 'valid',
      referenceNumber: 'HIT-2026-B001',
    },
  ],
};

export const ANIMAL_ROSIE: AnimalEntity = {
  id: 'animal-rosie',
  type: 'animal',
  name: 'Rosie',
  status: 'active',
  lastUpdated: daysAgo(1),
  properties: {
    ear_tag: 'DE 034 0123 45602',
    species: 'cattle',
    animal_type: 'Milchkuh',
    breed: 'Holstein-Friesian',
    date_of_birth: '2020-06-25',
    sex: 'female',
    weight: '640 kg',
    housing: 'Laufstall',
    hi_tier_nr: 'DE 034 0123456',
  },
  documents: [],
};

export const ANIMAL_BRUNO: AnimalEntity = {
  id: 'animal-bruno',
  type: 'animal',
  name: 'Bruno',
  status: 'active',
  lastUpdated: daysAgo(5),
  properties: {
    ear_tag: 'DE 034 0123 45615',
    species: 'cattle',
    animal_type: 'Mastbulle',
    breed: 'Fleckvieh',
    date_of_birth: '2023-09-08',
    sex: 'male',
    weight: '520 kg',
    housing: 'Laufstall',
    hi_tier_nr: 'DE 034 0123456',
  },
  documents: [],
};

// --- Pigs (Schweine) ---

export const ANIMAL_PIG_101: AnimalEntity = {
  id: 'animal-pig-101',
  type: 'animal',
  name: 'Mastschwein #101',
  status: 'active',
  lastUpdated: daysAgo(2),
  properties: {
    ear_tag: 'DE 034 0567 00101',
    species: 'pigs',
    animal_type: 'Mastschwein',
    breed: 'DL × Pietrain',
    date_of_birth: '2025-08-14',
    sex: 'male',
    weight: '95 kg',
    housing: 'Stall 1',
    hi_tier_nr: 'DE 034 0567890',
  },
  documents: [],
};

export const ANIMAL_PIG_102: AnimalEntity = {
  id: 'animal-pig-102',
  type: 'animal',
  name: 'Mastschwein #102',
  status: 'active',
  lastUpdated: daysAgo(2),
  properties: {
    ear_tag: 'DE 034 0567 00102',
    species: 'pigs',
    animal_type: 'Mastschwein',
    breed: 'DL × Pietrain',
    date_of_birth: '2025-08-14',
    sex: 'female',
    weight: '88 kg',
    housing: 'Stall 1',
    hi_tier_nr: 'DE 034 0567890',
  },
  documents: [],
};

export const ANIMAL_SAU_200: AnimalEntity = {
  id: 'animal-sau-200',
  type: 'animal',
  name: 'Zuchtsau #200',
  status: 'active',
  lastUpdated: daysAgo(3),
  properties: {
    ear_tag: 'DE 034 0567 00200',
    species: 'pigs',
    animal_type: 'Zuchtsau',
    breed: 'Deutsche Landrasse',
    date_of_birth: '2022-11-03',
    sex: 'female',
    weight: '185 kg',
    housing: 'Abferkelstall',
    hi_tier_nr: 'DE 034 0567890',
  },
  documents: [],
};

/**
 * All animal entities
 */
export const ALL_ANIMALS: AnimalEntity[] = [
  ANIMAL_BELLA,
  ANIMAL_ROSIE,
  ANIMAL_BRUNO,
  ANIMAL_PIG_101,
  ANIMAL_PIG_102,
  ANIMAL_SAU_200,
];

/**
 * Get animal by ID
 */
export function getAnimalById(id: string): AnimalEntity | undefined {
  return ALL_ANIMALS.find((a) => a.id === id);
}

/** @deprecated Use getAnimalById */
export const getAnimalGroupById = getAnimalById;

/**
 * Get total livestock units for all animals
 */
const GV_FACTORS: Record<string, number> = {
  Milchkuh: 1.0,
  Mastbulle: 0.6,
  Mastschwein: 0.4,
  Zuchtsau: 0.5,
};

export function calculateGV(animal: AnimalEntity): number {
  return GV_FACTORS[animal.properties.animal_type] ?? 0.3;
}

export function getTotalGV(): number {
  return ALL_ANIMALS.reduce((sum, a) => sum + calculateGV(a), 0);
}

// Deprecated re-exports
/** @deprecated */ export const ANIMALS_MASTSCHWEINE = ANIMAL_PIG_101;
/** @deprecated */ export const ANIMALS_MILCHKUEHE = ANIMAL_BELLA;
